"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import { formatDate } from "@/utils/commonFunctions";
import { submitAnswer } from "@/lib/prismaController";
import Icon from "@/components/icons/Icons";
import {
  handleField,
  validateInputData,
  ErrorMap,
} from "@/utils/allValidations";

export interface AnswerItem {
  answer_id: number;
  client_name: string | null;
  answer_date: string | null;
  answer: string;
}

export interface QuestionDetailViewProps {
  questionInfo: {
    page_id: number | null;
    question: string;
    question_date: string | null;
    question_author: string | null;
    slug: string;
  };
  initialAnswers: AnswerItem[];
}

export default function QuestionDetailView({
  questionInfo,
  initialAnswers,
}: QuestionDetailViewProps) {
  const [userName, setUserName] = useState("");
  const [errors, setErrors] = useState<ErrorMap>({});
  const [answerCharCount, setAnswerCharCount] = useState(0);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUl, setIsUl] = useState(false);
  const [isOl, setIsOl] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [copiedAnswerId, setCopiedAnswerId] = useState<number | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Author details for the question
  const questionAuthor = questionInfo.question_author || "Community Member";
  const authorInitial = questionAuthor.charAt(0).toUpperCase() || "Q";
  const formattedQuestionDate = questionInfo.question_date
    ? formatDate(questionInfo.question_date)
    : null;

  // Query formatting states to reflect active buttons in toolbar
  const updateActiveStates = useCallback(() => {
    if (typeof document !== "undefined") {
      try {
        setIsBold(document.queryCommandState("bold"));
        setIsItalic(document.queryCommandState("italic"));
        setIsUl(document.queryCommandState("insertUnorderedList"));
        setIsOl(document.queryCommandState("insertOrderedList"));
      } catch {
        // queryCommandState may fail if editor doesn't have active selection
      }
    }
  }, []);

  // Listen for selection changes inside editor to dynamically toggle toolbar buttons
  useEffect(() => {
    const handleSelectionChange = () => {
      if (editorRef.current && document.activeElement === editorRef.current) {
        updateActiveStates();
      }
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [updateActiveStates]);

  // Execute formatting command without losing focus/selection
  const handleCommand = (
    command: "bold" | "italic" | "insertUnorderedList" | "insertOrderedList",
  ) => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false);
    updateActiveStates();

    if (editorRef.current) {
      const text = editorRef.current.innerText || "";
      setAnswerCharCount(text.length);
      setIsEmpty(text.trim().length === 0);
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || "";
      const trimmed = text.trim();
      setAnswerCharCount(text.length);
      setIsEmpty(trimmed.length === 0);

      if (text.length > 5000) {
        setErrors((prev) => ({
          ...prev,
          answer: "Answer cannot exceed 5000 characters.",
        }));
      } else if (errors.answer) {
        setErrors((prev) => {
          if (!prev.answer) return prev;
          const updated = { ...prev };
          delete updated.answer;
          return updated;
        });
      }
    }
    updateActiveStates();
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      editorRef.current &&
      editorRef.current.innerText.length >= 5000 &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key) &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      setErrors((prev) => ({
        ...prev,
        answer: "Maximum limit of 5000 characters reached",
      }));
    }
  };

  const handleEditorPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const currentLength = editorRef.current?.innerText.length || 0;
    const remaining = Math.max(0, 5000 - currentLength);
    const textToInsert = text.slice(0, remaining);
    document.execCommand("insertText", false, textToInsert);
    handleEditorInput();
  };

  const handleNameChange = (val: string) => {
    let value = val;
    if (value.length > 100) {
      value = value.slice(0, 100);
    }
    handleField({
      type: "name",
      value,
      field: "name",
      mode: "change",
      setValue: setUserName,
      setErrors,
      prevValue: userName,
      allowSpace: true,
      validateOnChange: false,
    });
  };

  const handleNameBlur = () => {
    handleField({
      type: "name",
      value: userName,
      field: "name",
      mode: "blur",
      setErrors,
      prevValue: userName,
      allowSpace: true,
    });
  };

  const handleShare = (answerId: number) => {
    if (typeof window !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      setCopiedAnswerId(answerId);
      setTimeout(() => setCopiedAnswerId(null), 2000);
    }
  };

  const handleSubmitAnswer = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);

    const isNameValid = validateInputData("name", userName);
    const editor = editorRef.current;
    const plainText = editor ? editor.innerText.trim() : "";
    const isAnswerValid = validateInputData("message", plainText);

    let hasError = false;
    const newErrors: ErrorMap = {};

    if (!userName.trim()) {
      newErrors.name = "Please enter your name";
      hasError = true;
      nameInputRef.current?.focus();
    } else if (!isNameValid) {
      newErrors.name = "Please enter a valid name";
      hasError = true;
      nameInputRef.current?.focus();
    }

    if (!plainText) {
      newErrors.answer = "Please enter your answer before posting";
      hasError = true;
      if (!newErrors.name) editor?.focus();
    } else if (plainText.length < 10) {
      newErrors.answer =
        "Please provide a more detailed answer (minimum 10 characters)";
      hasError = true;
      if (!newErrors.name) editor?.focus();
    } else if (plainText.length > 5000) {
      newErrors.answer = "Answer cannot exceed 5000 characters";
      hasError = true;
      if (!newErrors.name) editor?.focus();
    }

    if (hasError) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    if (!questionInfo.page_id) {
      setFeedback({
        type: "error",
        message: "Question ID not found. Unable to post answer.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const htmlContent = editor ? editor.innerHTML.trim() : "";

      const res = await submitAnswer({
        page_id: questionInfo.page_id,
        answer: htmlContent,
        client_name: userName.trim(),
      });

      if (res.success) {
        setFeedback({
          type: "success",
          message:
            "Your answer has been submitted successfully and is pending approval.",
        });

        if (editor) {
          editor.innerHTML = "";
        }
        setAnswerCharCount(0);
        setIsEmpty(true);
        updateActiveStates();
      } else {
        setFeedback({
          type: "error",
          message:
            typeof res.message === "string"
              ? res.message
              : "Failed to submit answer. Please try again.",
        });
      }
    } catch {
      setFeedback({
        type: "error",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Back to questions link */}
      <Link
        href="/questions"
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-500 hover:text-primaryBlue transition-colors group mb-4"
      >
        <span className="group-hover:-translate-x-0.5 transition-transform">
          &larr;
        </span>
        Back to Community Q&A
      </Link>

      {/* 1. Main Question Card */}
      <h1 className="text-xl sm:text-2xl sm:leading-8 lg:text-3xl lg:leading-9 font-extrabold text-blue-900 leading-snug tracking-tight mb-3">
        {questionInfo.question}
      </h1>

      {/* Author info */}
      <div className="flex items-center gap-3 mb-6">
        <span className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center shrink-0 uppercase">
          {authorInitial}
        </span>
        <div className="space-y-0.5">
          <p className="block font-semibold text-sm sm:text-base sm:leading-6 text-slate-900 leading-tight">
            {questionAuthor}
          </p>
          {formattedQuestionDate && (
            <div className="block text-xs text-slate-500">
              Asked on {formattedQuestionDate}
            </div>
          )}
        </div>
      </div>

      {/* 2. Answers Section Header */}
      <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">
        {initialAnswers.length}{" "}
        {initialAnswers.length === 1 ? "Answer" : "Answers"}
      </h2>

      {/* 3. Answers List */}
      {initialAnswers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-4xl sm:rounded-6xl p-5 sm:p-8 text-center shadow-xs mb-5">
          <p className="text-base font-semibold text-slate-900 mb-1">
            No answers yet
          </p>
          <p className="text-sm text-slate-500">
            Be the first to share your knowledge with the community below!
          </p>
        </div>
      ) : (
        <div className="space-y-5 mb-5">
          {initialAnswers.map((ans) => {
            const authorName = ans.client_name?.trim() || "Community Member";
            const authorInitial = authorName.charAt(0).toUpperCase() || "A";
            const formattedDate = ans.answer_date
              ? formatDate(ans.answer_date)
              : null;
            const isCopied = copiedAnswerId === ans.answer_id;

            return (
              <div
                key={ans.answer_id}
                className="bg-white border border-slate-200 rounded-4xl sm:rounded-6xl p-4 sm:p-6"
              >
                {/* Author Info with Profile Letter and Date */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center shrink-0 uppercase">
                    {authorInitial}
                  </div>
                  <div>
                    <span className="block font-semibold text-sm sm:text-base sm:leading-6 text-slate-900 leading-tight">
                      {authorName}
                    </span>
                    {formattedDate && (
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {formattedDate}
                      </span>
                    )}
                  </div>
                </div>

                {/* Answer Content (HTML rendering with clean prose styles) */}
                <div
                  className="text-sm text-slate-700 leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_ol]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:my-3 [&_li]:leading-relaxed [&_strong]:font-semibold [&_strong]:text-slate-900 [&_b]:font-semibold [&_b]:text-slate-900 [&_a]:text-primaryBlue [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: ans.answer }}
                />

                {/* Answer Footer with Share action */}
                <div className="flex items-center justify-end gap-3 mt-4 pt-3.5 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => handleShare(ans.answer_id)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-primaryBlue transition-colors cursor-pointer"
                  >
                    <Icon.sharedFundIconV2 className="w-3.5 h-3.5 shrink-0" />
                    {isCopied ? "Link Copied!" : "Share"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 4. Submit Answer Form */}
      <div className="text-base sm:text-lg font-bold text-slate-900 mb-4">
        Your Answer
      </div>

      {feedback && (
        <div
          className={`p-3.5 rounded-xl text-xs sm:text-sm font-medium mb-4 ${
            feedback.type === "success"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {feedback.message}
        </div>
      )}

      <form onSubmit={handleSubmitAnswer} className="space-y-4">
        {/* User Name Input */}
        <div>
          <div className="flex items-center justify-between mb-1.5 sm:max-w-md">
            <label
              htmlFor="client_name"
              className="block text-xs font-semibold text-slate-600"
            >
              Your Name <span className="text-red-500">*</span>
            </label>
            <span className="text-2xs text-slate-400">
              {userName.length}/100
            </span>
          </div>
          <input
            id="client_name"
            ref={nameInputRef}
            type="text"
            maxLength={100}
            value={userName}
            onChange={(e) => handleNameChange(e.target.value)}
            onBlur={handleNameBlur}
            placeholder="e.g. Rahul Sharma"
            className={`w-full sm:max-w-md px-4 py-2.5 rounded-xl border text-xs sm:text-sm text-slate-900 placeholder-slate-400 outline-hidden transition bg-white ${
              errors.name
                ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                : "border-slate-300 focus:border-primaryBlue focus:ring-2 focus:ring-primaryBlue/20"
            }`}
          />
          {errors.name && (
            <p className="text-2xs text-red-500 mt-1">{errors.name}</p>
          )}
        </div>

        {/* Visual WYSIWYG Editor Container */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-slate-600">
              Your Answer <span className="text-red-500">*</span>
            </label>
            <span
              className={`text-2xs ${
                answerCharCount >= 5000
                  ? "text-red-500 font-semibold"
                  : "text-slate-400"
              }`}
            >
              {answerCharCount}/5000
            </span>
          </div>

          <div
            className={`border rounded-xl overflow-hidden transition-all bg-white ${
              errors.answer
                ? "border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100"
                : "border-slate-300 focus-within:border-primaryBlue focus-within:ring-2 focus-within:ring-primaryBlue/20"
            }`}
          >
            {/* Toolbar: Bold, Italic, Bullet List, Numbered List */}
            <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center gap-1.5">
              {/* Bold Button */}
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCommand("bold");
                }}
                title="Bold"
                aria-label="Format Bold"
                className={`w-7 h-7 flex items-center justify-center rounded-sm text-xs font-bold transition cursor-pointer ${
                  isBold
                    ? "bg-primaryBlue text-white shadow-xs"
                    : "text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                }`}
              >
                B
              </button>

              {/* Italic Button */}
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCommand("italic");
                }}
                title="Italic"
                aria-label="Format Italic"
                className={`w-7 h-7 flex items-center justify-center rounded-sm text-xs italic font-serif transition cursor-pointer ${
                  isItalic
                    ? "bg-primaryBlue text-white shadow-xs"
                    : "text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                }`}
              >
                I
              </button>

              <span className="w-px h-4 bg-slate-300 mx-1" />

              {/* Bullet List Button */}
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCommand("insertUnorderedList");
                }}
                title="Bullet List"
                aria-label="Format Bullet List"
                className={`w-7 h-7 flex items-center justify-center rounded-sm text-xs transition cursor-pointer ${
                  isUl
                    ? "bg-primaryBlue text-white shadow-xs"
                    : "text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                }`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              {/* Numbered List Button */}
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCommand("insertOrderedList");
                }}
                title="Numbered List"
                aria-label="Format Numbered List"
                className={`w-7 h-7 flex items-center justify-center rounded-sm text-xs font-semibold transition cursor-pointer ${
                  isOl
                    ? "bg-primaryBlue text-white shadow-xs"
                    : "text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                }`}
              >
                1.
              </button>
            </div>

            {/* Visual Editable Canvas with Placeholder */}
            <div className="relative">
              {isEmpty && (
                <div
                  onClick={() => editorRef.current?.focus()}
                  className="absolute top-4 left-4 text-xs sm:text-sm text-slate-400 pointer-events-none select-none"
                >
                  Share your knowledge with the community...
                </div>
              )}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                onPaste={handleEditorPaste}
                onKeyUp={updateActiveStates}
                onMouseUp={updateActiveStates}
                className="w-full p-4 text-xs sm:text-sm sm:leading-5 text-slate-900 outline-hidden min-h-35 max-h-400 overflow-y-auto leading-relaxed [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:my-1"
              />
            </div>
          </div>
          {errors.answer && (
            <p className="text-2xs text-red-500 mt-1">{errors.answer}</p>
          )}
          <p className="text-2xs text-slate-400 mt-1.5">
            Maximum 5000 characters. Be constructive and respect the community guidelines.
          </p>
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2.5 rounded-xl bg-primaryBlue hover:bg-primaryBlue/90 text-white font-semibold text-xs sm:text-sm transition shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer inline-flex items-center gap-2"
          >
            {isSubmitting ? "Posting Answer..." : "Post Answer"}
          </button>
        </div>
      </form>
    </>
  );
}
